import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Compass, Target } from 'lucide-react';
import { Link } from 'react-router-dom';
import { SEO } from '../components/SEO';
import Magnetic from '../components/Magnetic';
import { EASE, ParallaxImg, RevealWord, fadeUp } from '../components/motion';

/* ------------------------------------------------------------------- data */

const coreValues = [
  'Drive innovation through business excellence and strategic thinking.',
  'Deliver exceptional value and measurable results for our clients.',
  'Foster collaborative partnerships built on trust and transparency.',
  'Embrace continuous learning and professional growth.',
  'Maintain the highest standards of quality in every project.',
  'Promote sustainable and responsible business practices.',
  'Celebrate diversity and cultural understanding.',
];

/* ------------------------------------------------------------------- page */

const Vision = () => {
  return (
    <>
      <SEO
        title="Vision & Mission | DKSN Business Solutions"
        description="Our vision and mission: to be the catalyst for business transformation through innovative solutions and strategic excellence in Mthatha and across South Africa."
        path="/vision"
      />
      <div className="min-h-screen overflow-hidden bg-primary-950 text-white">
        {/* ============================================== HERO — manifesto */}
        <section className="relative overflow-hidden pb-20 pt-40 sm:pb-28 sm:pt-48">
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(900px_500px_at_15%_10%,rgba(91,141,239,0.13),transparent_60%)]" />
          <div className="section-container relative">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.3, ease: EASE }}
              className="mb-6 font-display text-sm font-semibold tracking-[0.12em] text-azure-500"
            >
              WHERE WE'RE HEADED
            </motion.div>
            <h1 className="mb-10 max-w-4xl font-display text-5xl font-bold leading-[1.02] tracking-[-0.045em] sm:text-6xl lg:text-7xl xl:text-[5.5rem]">
              <RevealWord delay={0.12}>Ambition, backed</RevealWord>
              <br />
              <RevealWord delay={0.26}>
                by a{' '}
                <span className="font-serif font-normal italic text-azure-300">plan.</span>
              </RevealWord>
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.55, ease: EASE }}
              className="max-w-2xl text-[17.5px] leading-[1.7] text-gray-300"
            >
              Everything we build at DKSN traces back to two statements — what we're working
              toward, and how we get there.
            </motion.p>
          </div>
        </section>

        {/* ====================================== VISION / MISSION — panels */}
        <section className="section-container">
          <div className="grid gap-4 lg:grid-cols-2">
            <motion.div
              {...fadeUp}
              className="relative overflow-hidden rounded-3xl border border-white/[0.09] bg-gradient-to-br from-azure-500/[0.09] to-azure-500/[0.02] p-10 sm:p-14"
            >
              <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-azure-500/20 blur-3xl" />
              <div className="relative mb-6 inline-flex rounded-xl bg-azure-500/10 p-3">
                <Target className="h-6 w-6 text-azure-300" />
              </div>
              <span className="relative block text-sm font-semibold uppercase tracking-[0.14em] text-azure-400">
                Our Vision
              </span>
              <p className="relative mt-7 font-serif text-2xl italic leading-[1.4] tracking-[-0.01em] text-white sm:text-3xl">
                To be the catalyst for business transformation through innovative solutions and
                strategic excellence — setting new standards in business solutions.
              </p>
            </motion.div>
            <motion.div
              {...fadeUp}
              transition={{ duration: 1, delay: 0.12, ease: EASE }}
              className="rounded-3xl border border-white/[0.09] bg-white/[0.02] p-10 sm:p-14"
            >
              <div className="mb-6 inline-flex rounded-xl bg-azure-500/10 p-3">
                <Compass className="h-6 w-6 text-azure-300" />
              </div>
              <span className="block text-sm font-semibold uppercase tracking-[0.14em] text-azure-400">
                Our Mission
              </span>
              <p className="mt-7 font-serif text-2xl italic leading-[1.4] tracking-[-0.01em] text-white sm:text-3xl">
                To empower businesses with comprehensive solutions that drive growth, foster
                innovation, and create lasting impact in an ever-evolving market.
              </p>
            </motion.div>
          </div>
        </section>

        {/* ========================================= VALUES — numbered list */}
        <section className="section-container pt-16 sm:pt-36">
          <div className="grid gap-14 lg:grid-cols-[0.9fr_1.1fr] lg:gap-20">
            <motion.div {...fadeUp} className="lg:sticky lg:top-32 lg:self-start">
              <span className="text-sm font-semibold uppercase tracking-[0.14em] text-azure-400">
                What Guides Us
              </span>
              <h2 className="mt-5 font-display text-4xl font-bold leading-[1.02] tracking-[-0.04em] sm:text-5xl lg:text-6xl">
                Seven things we{' '}
                <span className="font-serif font-normal italic text-azure-300">
                  refuse to compromise.
                </span>
              </h2>
              <p className="mt-6 max-w-sm leading-relaxed text-gray-500">
                Our core values aren't wall art. They're the checklist every project passes
                before it leaves the studio.
              </p>
            </motion.div>
            <div>
              {coreValues.map((value, i) => (
                <motion.div
                  key={value}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.4 }}
                  transition={{ duration: 0.7, delay: (i % 4) * 0.06, ease: EASE }}
                  className={`flex items-baseline gap-7 border-t border-white/10 py-7 ${
                    i === coreValues.length - 1 ? 'border-b' : ''
                  }`}
                >
                  <span className="flex-shrink-0 font-display text-sm font-semibold tracking-[0.08em] text-azure-500">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <p className="text-lg leading-[1.55] text-gray-300 sm:text-xl">{value}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* =========================================================== CTA */}
        <section className="relative mt-16 overflow-hidden sm:mt-40">
          <ParallaxImg
            src="https://images.unsplash.com/photo-1553877522-43269d4ea984?w=2000&auto=format&fit=crop&q=75"
            alt="DKSN team planning session"
            speed={18}
            className="absolute inset-0"
            overlayClassName="bg-gradient-to-b from-primary-950 via-primary-950/85 to-primary-950/90"
          />
          <motion.div
            {...fadeUp}
            className="relative mx-auto max-w-3xl px-4 py-16 text-center sm:px-6 sm:py-44"
          >
            <h2 className="mb-5 font-display text-5xl font-bold leading-[1.02] tracking-[-0.045em] sm:text-6xl lg:text-7xl">
              Let's put the mission{' '}
              <span className="font-serif font-normal italic text-azure-300">to work.</span>
            </h2>
            <p className="mx-auto mb-10 max-w-lg text-lg leading-relaxed text-gray-300">
              Tell us what you're building — we'll show you how DKSN fits in.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3.5">
              <Magnetic>
                <Link
                  to="/quote"
                  className="group inline-flex items-center gap-2.5 rounded-full bg-gray-100 px-9 py-[19px] text-base font-semibold text-primary-950 shadow-[0_20px_60px_-18px_rgba(255,255,255,0.55)] transition-colors hover:bg-white"
                >
                  Request a Quote
                  <ArrowRight className="h-[18px] w-[18px] transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </Magnetic>
              <a
                href="https://wa.me/27679210514"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center rounded-full border border-white/20 bg-white/5 px-7 py-[19px] text-base font-medium text-white backdrop-blur-md transition-colors hover:bg-white/15"
              >
                WhatsApp Us
              </a>
            </div>
          </motion.div>
        </section>
      </div>
    </>
  );
};

export default Vision;
